import React, { useState, useRef, useEffect, useCallback } from 'react';
import Box from '../uikit/box/Box';
import Text from '../uikit/text/Text';
import Icon from '../uikit/icon/Icon';
import { FiChevronUp, FiChevronDown } from 'react-icons/fi';
import { sanitizeHtml } from '../../lib/sanitizeHtml';

/**
 * Long-form meeting content that must not swallow the viewport.
 *
 * Requested by the client on 2026-08-07: an AI meeting summary and its
 * follow-ups can run long enough to push everything else off the monitor, so
 * both need to expand and contract.
 *
 * <ClampedText> clamps prose to N lines and reveals the rest. A collapsed
 * summary still shows its opening sentences, so the page stays scannable rather
 * than becoming a row of headings. The toggle hides itself entirely when the
 * content already fits, so a short meeting does not grow a control that does
 * nothing.
 *
 * Both the summary and the follow-ups are prose blobs today (`meeting.summary`
 * and `meeting.ai_insights`), so one component covers both. If the follow-ups
 * ever become the structured next_steps array from the scorer, they want a
 * list-shaped sibling that collapses by item count instead of by line.
 *
 * Existing collapse UI in this app (Attendee/Reviewer in MeetingDetail.js) uses
 * the same FiChevronUp/FiChevronDown pair; keep that vocabulary.
 */

// A real <button>, so the toggle is keyboard-operable and announced as a
// control. Box's `el` prop swaps the tag; the style resets strip the native
// chrome that would otherwise fight the page.
const Toggle = ({ open, onToggle, openLabel, closedLabel }) => (
  <Box
    el="button"
    type="button"
    display="flex"
    alignItems="center"
    mt={2}
    onClick={onToggle}
    aria-expanded={open}
    style={{
      cursor: 'pointer',
      width: 'fit-content',
      background: 'none',
      border: 0,
      padding: 0,
      font: 'inherit',
    }}
  >
    <Text
      fontFamily="montserrat"
      fontSize={1}
      fontWeight="bold"
      textTransform="uppercase"
      color="primarys.0"
    >
      {open ? openLabel : closedLabel}
    </Text>
    <Icon
      ml={1}
      size={16}
      color="primarys.0"
      icon={open ? FiChevronUp : FiChevronDown}
      style={{ transform: 'translateY(-1px)' }}
    />
  </Box>
);

/**
 * Clamp prose to `lines` and offer a toggle only when it actually overflows.
 *
 * Whether the text overflows depends on the rendered width, so it cannot be
 * decided from the string: it is measured after layout and re-measured on
 * resize. scrollHeight is compared against clientHeight while the clamp is
 * applied, with 1px of slack for sub-pixel line heights.
 */
export const ClampedText = ({
  children,
  html,
  lines = 4,
  openLabel = 'Show less',
  closedLabel = 'Show more',
  ...rest
}) => {
  const [open, setOpen] = useState(false);
  const [overflowing, setOverflowing] = useState(false);
  const ref = useRef(null);

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setOverflowing(el.scrollHeight - el.clientHeight > 1);
  }, []);

  useEffect(() => {
    // Only meaningful while clamped; expanded, scrollHeight === clientHeight.
    if (!open) measure();
  }, [children, open, lines, measure]);

  useEffect(() => {
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure]);

  if (!children && !html) return null;

  const clamp = open
    ? {}
    : {
        display: '-webkit-box',
        WebkitLineClamp: lines,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
      };

  return (
    <Box {...rest}>
      {/*
        Box, not Text: Text is a plain function component and drops refs, and
        the clamp has to be measured on the element that carries it. Box wraps
        CleanElement's forwardRef, so the ref reaches the DOM node.
      */}
      <Box
        ref={ref}
        // A div when rendering markup: the summary carries its own <p> and <ul>,
        // and block elements inside a <p> are invalid and get reparented by the
        // browser, which breaks the clamp measurement along with the layout.
        el={html ? 'div' : 'p'}
        fontFamily="montserrat"
        fontSize={1}
        lineHeight={3}
        color="primarys.0"
        style={{ margin: 0, ...clamp }}
        {...(html
          ? { dangerouslySetInnerHTML: { __html: sanitizeHtml(html) } }
          : {})}
      >
        {html ? undefined : children}
      </Box>
      {(overflowing || open) && (
        <Toggle
          open={open}
          onToggle={() => setOpen((s) => !s)}
          openLabel={openLabel}
          closedLabel={closedLabel}
        />
      )}
    </Box>
  );
};

export default ClampedText;
